import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { formatErrorForSnackbar, toApiError } from '@/__core/helpers/Error';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import AppListToolbarGlobal from '@/modules/inventory/requestMaterial/components/RequestMaterialToolbarGlobal';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import {
  resolveActiveCompanyFilter,
  resolveToolbarTabCompanyFilter,
} from '@/__core/hooks/useCompanyContext';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { RootState } from '@/setup/redux/RootReducer';
import { uploadMaterialRequestDocument } from '@/modules/documents/redux/DocumentCRUD';

import { createItemUomBackend, getItemUomBackendOptions } from '@/modules/inventory/items/redux/ItemUomCRUD';

import {
  createDefaultRequestMaterialForm,
  requestMaterialTypeOptions,
} from '../requestMaterialMockData';
import {
  createMaterialRequest,
  deleteMaterialRequest,
  getMaterialRequests,
  updateMaterialRequest,
} from '../redux/RequestMaterialCRUD';
import {
  createRequestMaterialStatus,
  getRequestMaterialStatuses,
  RequestMaterialStatusOption,
} from '../redux/RequestMaterialStatusCRUD';
import useRequestMaterialCompanyOptions from '../hooks/useRequestMaterialCompanyOptions';
import useRequestMaterialEmployees from '../hooks/useRequestMaterialEmployees';
import useRequestMaterialItems from '../hooks/useRequestMaterialItems';
import useRequestMaterialOrders from '../hooks/useRequestMaterialOrders';
import useRequestMaterialWarehouses from '../hooks/useRequestMaterialWarehouses';
import { RequestMaterialFormData, RequestMaterialItemOption, RequestMaterialLine, RequestMaterialListRow } from '../types';
import RequestMaterialForm from './RequestMaterialForm';
import RequestMaterialList from './RequestMaterialList';

const DEFAULT_REQUEST_MATERIAL_STATUS_CODE = 'YEU_CAU_VAT_TU';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const buildFormMetadata = (formData: RequestMaterialFormData, requestId?: string) => ({ formData, requestId });

const hasRequestMaterialChanges = (
  current: RequestMaterialFormData,
  updates: Partial<RequestMaterialFormData>,
) => Object.entries(updates).some(([field, value]) => {
  const currentValue = (current as any)[field];
  return typeof value === 'object'
    ? JSON.stringify(value) !== JSON.stringify(currentValue)
    : value !== currentValue;
});

const normalizeStatusValue = (value: string | null | undefined) => (value || '').trim().toUpperCase();

const getRequestMaterialErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as any)?.response?.data;
  const directMessage = data?.detail || data?.message || data?.error;

  if (typeof directMessage === 'string' && directMessage.trim()) {
    return directMessage.trim();
  }

  if (Array.isArray(directMessage) && directMessage.length > 0) {
    return directMessage.map(String).join(', ');
  }

  const formattedMessage = formatErrorForSnackbar(toApiError(error));
  return formattedMessage && formattedMessage !== 'An unexpected error occurred'
    ? formattedMessage
    : fallback;
};

const getDefaultRequestMaterialStatus = (options: RequestMaterialStatusOption[]) => {
  return options.find((option) => option.is_default === true)
    || options.find((option) => normalizeStatusValue(option.code) === DEFAULT_REQUEST_MATERIAL_STATUS_CODE)
    || options.find((option) => normalizeStatusValue(option.id) === DEFAULT_REQUEST_MATERIAL_STATUS_CODE)
    || null;
};

const resolveStatusId = (statusValue: string | null | undefined, options: RequestMaterialStatusOption[]) => {
  if (!statusValue) {
    return '';
  }

  const normalizedStatus = normalizeStatusValue(statusValue);
  const matched = options.find((option) => {
    return normalizeStatusValue(option.id) === normalizedStatus || normalizeStatusValue(option.code) === normalizedStatus;
  });

  const resolvedId = matched?.id || (UUID_PATTERN.test(statusValue) ? statusValue : '');
  return UUID_PATTERN.test(resolvedId) ? resolvedId : '';
};

export default function RequestMaterialModule() {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const companies = useSelector((state: RootState) => state.auth.companies);
  const globalRoles = useSelector((state: RootState) => state.auth.globalRoles);
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const showSuccess = useCallback((msg: string) => {
    setSnackbarSeverity('success');
    setSnackbarMessage(msg);
  }, []);

  const showError = useCallback((msg: string) => {
    setSnackbarSeverity('error');
    setSnackbarMessage(msg);
  }, []);

  const { isGlobal, companyOptions, initialCompanyId, normalizedSelectedCompanyId, ensureCompanyOptionsLoaded } = useRequestMaterialCompanyOptions({
    selectedCompany,
    companies,
    globalRoles,
    onError: showError,
  });

  const [requests, setRequests] = useState<RequestMaterialListRow[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listPagination, setListPagination] = useState({ count: 0, page: 1, pageSize: DEFAULT_PAGE_SIZE, totalPages: 1 });
  const [uomOptions, setUomOptions] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [formStatusOptions, setFormStatusOptions] = useState<RequestMaterialStatusOption[]>([]);

  useEffect(() => {
    getItemUomBackendOptions()
      .then((options) => setUomOptions(options.map((o) => ({ id: o.id, name: o.name, code: o.name }))))
      .catch(() => {});
  }, []);

  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [toolbarFilters, setToolbarFilters] = useState({
    company: 'all',
    status: 'all',
    requestType: 'all',
  });
  const [dirtyTabIds, setDirtyTabIds] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState(false);

  const toolbarCompanyId = useMemo(
    () => (toolbarFilters.company !== 'all' ? toolbarFilters.company : ''),
    [toolbarFilters.company],
  );

  const resolveFormCompanyId = useCallback((formData?: RequestMaterialFormData, tab?: TabData) => {
    const formCompanyId = String(formData?.company_id || '').trim();
    const tabCompanyId = String(tab?.metadata?.companyId || '').trim();
    return formCompanyId || tabCompanyId || toolbarCompanyId || normalizedSelectedCompanyId || initialCompanyId || '';
  }, [initialCompanyId, normalizedSelectedCompanyId, toolbarCompanyId]);

  const activeCompanyFilter = useMemo(
    () =>
      resolveActiveCompanyFilter(
        getCurrentCompanyId() || normalizedSelectedCompanyId || '',
        toolbarFilters.company
      ),
    [normalizedSelectedCompanyId, toolbarFilters.company]
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(toolbarFilters.company),
    [toolbarFilters.company]
  );

  const { canCreate: canCreateRequests, canDelete: canDeleteRequests, resolveTabPermissions, resolvePermissionsForCompanyId, canReadRecord } = useModuleToolbarPermissions({
    resource: 'inventory.materialrequest',
    activeCompanyFilter,
    updateTabTypes: ['update-request-material'],
  });
  const { resolvePermissionsForCompanyId: resolveItemPermissionsForCompanyId } = useModuleToolbarPermissions({
    resource: 'inventory.item',
    activeCompanyFilter,
  });

  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      { id: 0, title: 'Danh sách request', type: 'list', closable: false, createdAt: Date.now() },
    ];
    if (canCreateRequests) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreateRequests]);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_request_material_tabs',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreateRequests),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-request-material', 'update-request-material'],
    allowMultipleTabTypes: ['new-request-material', 'update-request-material'],
    buildNewTab: (currentTabs) => {
      const baseCompanyId =
        toolbarCompanyId ||
        normalizedSelectedCompanyId ||
        initialCompanyId ||
        '';
      const defaultStatus = getDefaultRequestMaterialStatus(formStatusOptions);
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `Request #${currentTabs.filter((tab) => tab.type === 'new-request-material').length + 1}`,
        type: 'new-request-material',
        closable: true,
        metadata: {
          companyId: baseCompanyId,
          ...buildFormMetadata({
            ...createDefaultRequestMaterialForm(baseCompanyId),
            status: defaultStatus?.id || createDefaultRequestMaterialForm(baseCompanyId).status,
            status_color: defaultStatus?.color || '',
          }),
        },
      };
    },
    onAfterTabChange: () => setSnackbarMessage(null),
  });

  const listCompanyId = useMemo(() => {
    if (!isGlobal && normalizedSelectedCompanyId) {
      return normalizedSelectedCompanyId;
    }
    return activeCompanyFilter && activeCompanyFilter !== 'all' ? activeCompanyFilter : undefined;
  }, [activeCompanyFilter, isGlobal, normalizedSelectedCompanyId]);

  const fetchRequests = useCallback(
    async (page = 1) => {
      setListLoading(true);
      try {
        const pageSize = DEFAULT_PAGE_SIZE;
        const result = await getMaterialRequests({
          companyId: listCompanyId,
          search: searchQuery || undefined,
          status: toolbarFilters.status,
          requestType: toolbarFilters.requestType,
          page,
          pageSize,
        });
        setRequests(result.results);
        setListPagination({ count: result.count, page, pageSize, totalPages: result.totalPages });
      } catch {
        showError('Không thể tải danh sách phiếu yêu cầu vật tư.');
      } finally {
        setListLoading(false);
      }
    },
    [listCompanyId, searchQuery, showError, toolbarFilters.requestType, toolbarFilters.status]
  );

  useEffect(() => {
    void fetchRequests(listPagination.page);
  }, [fetchRequests, listPagination.page]);

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as RequestMaterialFormData | undefined;
  const activeRequestId = (
    activeTabData?.metadata?.requestId
    || activeTabData?.entityId
  ) as string | undefined;
  const statusFilterOptions = useMemo(
    () => [{ id: 'all', name: 'Tất cả trạng thái' }, ...formStatusOptions],
    [formStatusOptions],
  );

  const formCompanyId = activeTabData?.type === 'list'
    ? (listCompanyId || toolbarCompanyId || '')
    : resolveFormCompanyId(activeFormData, activeTabData);
  const statusCompanyId = formCompanyId || listCompanyId || toolbarCompanyId || normalizedSelectedCompanyId || initialCompanyId || '';
  const { orderOptions, loading: orderLoading } = useRequestMaterialOrders({
    companyId: formCompanyId,
    search: activeFormData?.order_search || '',
    skip: !formCompanyId,
    onError: showError,
  });
  const { warehouseOptions } = useRequestMaterialWarehouses({
    companyId: formCompanyId,
    skip: !formCompanyId,
    onError: showError,
  });

  useEffect(() => {
    if (
      !activeTabData?.id
      || activeTabData.type !== 'new-request-material'
      || !activeFormData
      || activeFormData.warehouse_id
      || warehouseOptions.length === 0
    ) {
      return;
    }

    const defaultWarehouse = warehouseOptions.find((warehouse) => warehouse.is_default);
    if (!defaultWarehouse) {
      return;
    }

    updateTab(activeTabData.id, {
      metadata: {
        ...(activeTabData.metadata || {}),
        ...buildFormMetadata({
          ...activeFormData,
          warehouse_id: defaultWarehouse.id,
        }, activeRequestId),
      },
    });
  }, [activeFormData, activeRequestId, activeTabData, updateTab, warehouseOptions]);
  const { itemOptions, addOption: addItemOption } = useRequestMaterialItems({
    companyId: formCompanyId,
    skip: !formCompanyId,
    onError: showError,
  });
  const { employeeOptions, currentEmployeeId } = useRequestMaterialEmployees({
    companyId: formCompanyId,
    userId: currentUser?.id ? String(currentUser.id) : '',
    skip: !formCompanyId,
    onError: showError,
  });
  const requesterDefaultedTabIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const tabId = activeTabData?.id;
    if (
      !tabId
      || activeTabData?.type !== 'new-request-material'
      || !activeFormData
      || !currentEmployeeId
      || activeFormData.requested_by_id
      || requesterDefaultedTabIdsRef.current.has(tabId)
    ) {
      return;
    }

    requesterDefaultedTabIdsRef.current.add(tabId);
    updateTab(tabId, {
      metadata: {
        ...(activeTabData.metadata || {}),
        ...buildFormMetadata({
          ...activeFormData,
          requested_by_id: currentEmployeeId,
        }, activeRequestId),
      },
    });
  }, [activeFormData, activeRequestId, activeTabData, currentEmployeeId, updateTab]);

  useEffect(() => {
    let cancelled = false;

    getRequestMaterialStatuses(statusCompanyId || undefined)
      .then((options) => {
        if (cancelled) {
          return;
        }
        setFormStatusOptions(options);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        showError('Không thể tải danh sách trạng thái request vật tư.');
      });

    return () => {
      cancelled = true;
    };
  }, [showError, statusCompanyId]);

  useEffect(() => {
    const defaultStatus = getDefaultRequestMaterialStatus(formStatusOptions);
    if (!defaultStatus && formStatusOptions.length === 0) {
      return;
    }

    storedTabs.forEach((tab) => {
      if (tab.type !== 'new-request-material' && tab.type !== 'update-request-material') {
        return;
      }

      const tabFormData = tab.metadata?.formData as RequestMaterialFormData | undefined;
      if (!tabFormData) {
        return;
      }

      const resolvedStatusId = resolveStatusId(tabFormData.status, formStatusOptions);

      if (tab.type === 'update-request-material') {
        if (!resolvedStatusId || resolvedStatusId === tabFormData.status) {
          return;
        }

        updateTab(tab.id, {
          metadata: buildFormMetadata({
            ...tabFormData,
            status: resolvedStatusId,
          }, tab.metadata?.requestId as string | undefined),
        });
        return;
      }

      if (!defaultStatus) {
        return;
      }

      const normalizedCurrentStatus = normalizeStatusValue(tabFormData.status);
      const matchedStatus = formStatusOptions.find((option) => {
        return normalizeStatusValue(option.id) === normalizedCurrentStatus
          || normalizeStatusValue(option.code) === normalizedCurrentStatus;
      });

      if (matchedStatus && normalizedCurrentStatus !== 'REQUEST' && normalizedCurrentStatus !== DEFAULT_REQUEST_MATERIAL_STATUS_CODE) {
        if (matchedStatus.id !== tabFormData.status) {
          updateTab(tab.id, {
            metadata: buildFormMetadata({
              ...tabFormData,
              status: matchedStatus.id,
            }, tab.metadata?.requestId as string | undefined),
          });
        }
        return;
      }

      if (resolvedStatusId === defaultStatus.id) {
        return;
      }

      updateTab(tab.id, {
        metadata: buildFormMetadata({
          ...tabFormData,
          status: defaultStatus.id,
        }, tab.metadata?.requestId as string | undefined),
      });
    });
  }, [formStatusOptions, storedTabs, updateTab]);

  const handleToolbarFilterChange = useCallback((field: string, value: string) => {
    setListPagination((current) => ({ ...current, page: 1 }));
    setToolbarFilters((current) => ({ ...current, [field]: value }));
  }, []);

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
      setListPagination((current) => ({ ...current, page: 1 }));
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
    setListPagination((current) => ({ ...current, page: 1 }));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setListPagination((current) => ({ ...current, page }));
  };

  const updateFormData = (updates: Partial<RequestMaterialFormData>) => {
    if (!activeTabData?.id || !activeFormData) {
      return;
    }

    const isUpdateMode = Boolean(activeRequestId);
    const effectiveCompanyId = resolveFormCompanyId(activeFormData, activeTabData);
    const tabPermissions = resolvePermissionsForCompanyId(effectiveCompanyId);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    if (!hasRequestMaterialChanges(activeFormData, updates) && activeFormData.company_id === effectiveCompanyId) {
      return;
    }

    const nextFormData = { ...activeFormData, ...updates, company_id: effectiveCompanyId };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeRequestId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const updateLine = (lineId: number, updates: Partial<RequestMaterialLine>) => {
    if (!activeFormData) {
      return;
    }

    const isUpdateMode = Boolean(activeRequestId);
    const tabPermissions = resolvePermissionsForCompanyId(resolveFormCompanyId(activeFormData, activeTabData));
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextLines = activeFormData.lines.map((line) => line.id === lineId ? { ...line, ...updates } : line);
    updateFormData({ lines: nextLines });
  };

  const addLine = () => {
    if (!activeFormData) {
      return;
    }

    const isUpdateMode = Boolean(activeRequestId);
    const tabPermissions = resolvePermissionsForCompanyId(resolveFormCompanyId(activeFormData, activeTabData));
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextLine: RequestMaterialLine = {
      id: Date.now(),
      sort_order: activeFormData.lines.length + 1,
      line_type: 'item',
      requested_qty: 1,
    };
    updateFormData({ lines: [...activeFormData.lines, nextLine] });
  };

  const removeLine = (lineId: number) => {
    if (!activeFormData) {
      return;
    }

    const isUpdateMode = Boolean(activeRequestId);
    const tabPermissions = resolvePermissionsForCompanyId(resolveFormCompanyId(activeFormData, activeTabData));
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextLines = activeFormData.lines
      .filter((line) => line.id !== lineId)
      .map((line, index) => ({ ...line, sort_order: index + 1 }));
    updateFormData({ lines: nextLines.length > 0 ? nextLines : [{ id: Date.now(), sort_order: 1, line_type: 'item', requested_qty: 1 }] });
  };

  const handleItemCreated = (item: RequestMaterialItemOption) => {
    addItemOption(item);
  };

  const createUom = async (payload: { name: string; conversion?: { to_uom_id: string; ratio: number } }) => {
    const created = await createItemUomBackend(payload.name, payload.conversion);
    const newUom = { id: created.id, name: created.name, code: created.name };
    setUomOptions((current) => {
      if (current.some((o) => o.id === newUom.id)) return current;
      return [...current, newUom];
    });
    return newUom;
  };

  const createStatus = async (payload: { name: string }) => {
    const isUpdateMode = Boolean(activeRequestId);
    const effectiveCompanyId = resolveFormCompanyId(activeFormData, activeTabData);
    const tabPermissions = resolvePermissionsForCompanyId(effectiveCompanyId);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      throw new Error('Không có quyền tạo trạng thái.');
    }

    const nextStatus = await createRequestMaterialStatus({
      name: payload.name.trim(),
      company_id: effectiveCompanyId || statusCompanyId || undefined,
    });

    setFormStatusOptions((current) => {
      const existed = current.find(
        (option) => option.id === nextStatus.id || option.name.trim().toLowerCase() === nextStatus.name.trim().toLowerCase(),
      );
      if (existed) {
        return current;
      }
      return [...current, nextStatus];
    });

    return nextStatus;
  };

  const openExistingRequest = (row: RequestMaterialListRow) => {
    if (!canReadRecord(row)) {
      showError('Bạn không có quyền xem chi tiết phiếu yêu cầu vật tư.');
      return;
    }
    guardAndRun(() => {
         void (async () => {
            await ensureCompanyOptionsLoaded();
            const newTabId = Date.now() + Math.floor(Math.random() * 1000);
            const matchedStatus = formStatusOptions.find((s) => s.id === resolveStatusId(row.status, formStatusOptions));
            const openedIndex = openTab({
              id: newTabId,
              title: row.request_no,
              type: 'update-request-material',
              closable: true,
              entityId: row.id,
              metadata: {
                companyId: row.company_id,
                ...buildFormMetadata({
                request_type: row.request_type,
                company_id: row.company_id,
                warehouse_id: row.warehouse_id || '',
                order_id: row.order_id || '',
                order_search: '',
                note: row.note || '',
                status: resolveStatusId(row.status, formStatusOptions),
                status_color: matchedStatus?.color || '',
                requested_at: row.requested_at || null,
                required_by: row.required_by || null,
                requested_by_id: row.requested_by_id || null,
                lines: row.lines,
              }, row.id),
              },
            });
        if (openedIndex !== null && openedIndex !== undefined) {
          return;
        }
      })();
    });
  };

  const handleSave = async (files: File[] = []): Promise<string | false> => {
    if (!activeFormData || !activeTabData?.id || saving) {
      return false;
    }

    const isUpdateMode = Boolean(activeRequestId);
    const effectiveCompanyId = isUpdateMode
      ? resolveFormCompanyId(activeFormData, activeTabData)
      : (toolbarCompanyId || resolveFormCompanyId(activeFormData, activeTabData));
    const savePermissions = resolvePermissionsForCompanyId(effectiveCompanyId);
    if (isUpdateMode && !savePermissions.canUpdate) {
      showError('Bạn không có quyền cập nhật phiếu yêu cầu vật tư.');
      return false;
    }
    if (!isUpdateMode && !savePermissions.canCreate) {
      showError('Bạn không có quyền tạo phiếu yêu cầu vật tư.');
      return false;
    }
    if (!effectiveCompanyId) {
      showError('Không xác định được công ty. Vui lòng chọn công ty trên toolbar trước khi lưu.');
      return false;
    }

    const normalizedStatusId = resolveStatusId(activeFormData.status, formStatusOptions);
    if (!normalizedStatusId) {
      showError('Không xác định được trạng thái hợp lệ để lưu phiếu yêu cầu vật tư.');
      return false;
    }

    const normalizedFormData: RequestMaterialFormData = {
      ...activeFormData,
      company_id: effectiveCompanyId,
      status: normalizedStatusId,
      lines: activeFormData.lines.filter((line) => line.line_type === 'item'),
    };

    setSaving(true);
    try {
      let saved: RequestMaterialListRow;
      if (activeRequestId) {
        saved = await updateMaterialRequest(activeRequestId, normalizedFormData);
      } else {
        saved = await createMaterialRequest(normalizedFormData);
      }

      const nextId = saved.id || activeRequestId;
      let attachmentUploadFailed = false;
      if (nextId && files.length > 0) {
        try {
          await Promise.all(files.map((file) => uploadMaterialRequestDocument(nextId, file)));
        } catch {
          attachmentUploadFailed = true;
        }
      }

      // Refresh list
      void fetchRequests(listPagination.page);

      handleUnsavedChangesUpdate(false, activeTabData.id);
      setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: false }));
      showSuccess(attachmentUploadFailed
        ? `Đã lưu ${saved.request_no}, nhưng upload file đính kèm thất bại.`
        : (files.length > 0 ? `Đã lưu ${saved.request_no} và file đính kèm.` : `Đã lưu ${saved.request_no} ở trạng thái request.`));
      updateTab(activeTabData.id, {
        title: saved.request_no,
        entityId: nextId,
        metadata: buildFormMetadata(normalizedFormData, nextId),
      });
      return nextId || false;
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.non_field_errors?.[0] || err?.message || 'Có lỗi xảy ra khi lưu phiếu.';
      showError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      return false;
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType="inventory"
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as RequestMaterialFormData | undefined;
              const tabRequestId = (
                tab.metadata?.requestId
                || tab.entityId
              ) as string | undefined;
              const tabPermissions = resolveTabPermissions(tab);
              const tabCompanyId = resolveFormCompanyId(tabFormData, tab);
              const canCreateItemForTab = resolveItemPermissionsForCompanyId(tabCompanyId).canCreate;
              const tabCompanyName = companyOptions.find((company) => company.company_id === tabCompanyId)?.company_name
                || (tabCompanyId === normalizedSelectedCompanyId ? selectedCompany?.company_name : '');

              return (
                <Box
                  key={tab.id}
                  sx={{
                    display: isActive ? 'flex' : 'none',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  {tab.type === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() => setIsAdvancedSearchOpen((open) => !open)}
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: toolbarFilters.company || 'all',
                              status: toolbarFilters.status,
                              requestType: toolbarFilters.requestType,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            statusOptions={statusFilterOptions}
                            requestTypeOptions={requestMaterialTypeOptions}
                          />
                        }
                        dataTable={
                          <RequestMaterialList
                            rows={requests}
                            statusOptions={formStatusOptions}
                            loading={listLoading}
                            onSelectRow={openExistingRequest}
                            pagination={listPagination}
                            canDelete={canDeleteRequests}
                            onPageChange={handlePageChange}
                            onDeleteSelected={async (ids) => {
                              if (!canDeleteRequests) {
                                showError('Bạn không có quyền xóa phiếu yêu cầu vật tư.');
                                return;
                              }
                              try {
                                await Promise.all(ids.map((id) => deleteMaterialRequest(id)));
                                showSuccess('Đã xóa thành công.');
                                void fetchRequests(listPagination.page);
                              } catch (error) {
                                showError(getRequestMaterialErrorMessage(
                                  error,
                                  'Không thể xóa phiếu yêu cầu vật tư.',
                                ));
                                throw error;
                              }
                            }}
                          />
                        }
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2 }}>
                      <RequestMaterialForm
                        formData={{ ...tabFormData, company_id: tabCompanyId }}
                        warehouseOptions={warehouseOptions}
                        orderOptions={orderOptions}
                        orderLoading={orderLoading}
                        statusOptions={formStatusOptions}
                        itemOptions={itemOptions}
                        uomOptions={uomOptions}
                        employeeOptions={employeeOptions}
                        companyName={tabCompanyName}
                        materialRequestId={tabRequestId}
                        saving={saving}
                        isDirty={!!dirtyTabIds[tab.id]}
                        canEdit={tab.type === 'new-request-material' ? tabPermissions.canCreate : tabPermissions.canUpdate}
                        canCreateItem={canCreateItemForTab}

                        onChangeForm={(updates) => {
                          const canEditTab = tab.type === 'new-request-material' ? tabPermissions.canCreate : tabPermissions.canUpdate;
                          if (!canEditTab) {
                            return;
                          }
                          if (tab.id !== activeTabData?.id) {
                            if (!hasRequestMaterialChanges(tabFormData, updates)) {
                              return;
                            }
                            updateTab(tab.id, { metadata: buildFormMetadata({ ...tabFormData, ...updates, company_id: tabCompanyId }, tabRequestId) });
                            handleUnsavedChangesUpdate(true, tab.id);
                            setDirtyTabIds((current) => ({ ...current, [tab.id]: true }));
                            return;
                          }
                          updateFormData(updates);
                        }}
                        onChangeLine={(lineId, updates) => {
                          const canEditTab = tab.type === 'new-request-material' ? tabPermissions.canCreate : tabPermissions.canUpdate;
                          if (!canEditTab) {
                            return;
                          }
                          if (tab.id !== activeTabData?.id) {
                            const nextLines = tabFormData.lines.map((line) => line.id === lineId ? { ...line, ...updates } : line);
                            updateTab(tab.id, { metadata: buildFormMetadata({ ...tabFormData, lines: nextLines, company_id: tabCompanyId }, tabRequestId) });
                            handleUnsavedChangesUpdate(true, tab.id);
                            setDirtyTabIds((current) => ({ ...current, [tab.id]: true }));
                            return;
                          }
                          updateLine(lineId, updates);
                        }}
                        onAddItem={addLine}
                        onRemoveLine={removeLine}
                        onReorderLines={(lines) => updateFormData({ lines })}
                        onCreateStatus={createStatus}
                        onCreateItem={handleItemCreated}
                        onCreateUom={createUom}
                        onSave={handleSave}
                        onCancel={() => handleCloseTab(tab.id)}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={3500}
        onClose={() => setSnackbarMessage(null)}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
